import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  Clock,
  Globe,
  Search,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LogoHeaderImage } from "@/components/branding/LogoHeaderImage";
import { LOGO_HEADER_INTRINSIC } from "@/lib/logoHeaderImage";
import catererMapEurope from "@/assets/marketing/caterer-map-europe.png";

const FEATURES = [
  { icon: Globe, label: "Worldwide coverage" },
  { icon: Clock, label: "24/7 availability" },
  { icon: Users, label: "500+ caterers" },
] as const;

type GlobalCatererNetworkSectionProps = {
  className?: string;
  /** When true, enable landing reveal animations. */
  reveal?: boolean;
};

export function GlobalCatererNetworkSection({
  className,
  reveal = false,
}: GlobalCatererNetworkSectionProps) {
  return (
    <section
      id="global-network"
      className={cn("bg-muted/30 py-20 md:py-24", className)}
      {...(reveal ? { "data-reveal-section": true } : {})}
    >
      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.15fr)] lg:gap-12 xl:gap-14">
          <div className="max-w-xl">
            <div
              {...(reveal ? { "data-reveal-item": true } : {})}
              className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1.5 text-sm font-medium text-accent"
            >
              <Globe className="h-3.5 w-3.5" aria-hidden />
              Global caterer network
            </div>

            <h2
              {...(reveal ? { "data-reveal-item": true } : {})}
              className="marketing-display mt-5 text-balance text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-[2.75rem] lg:leading-[1.15]"
            >
              Find trusted caterers at any{" "}
              <span className="text-accent">airport</span>
            </h2>

            <p
              {...(reveal ? { "data-reveal-item": true } : {})}
              className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]"
            >
              Search by airport, ICAO, IATA or city and instantly discover verified aviation catering
              partners worldwide.
            </p>

            <div {...(reveal ? { "data-reveal-item": true } : {})} className="mt-8">
              <Button
                size="lg"
                className="h-12 w-full gap-2 bg-accent px-6 text-base hover:bg-accent/90 sm:w-auto"
                asChild
              >
                <Link to="/CFA/search-caterers">
                  <Search className="h-4 w-4" aria-hidden />
                  Explore the live map
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </div>

            <ul
              {...(reveal ? { "data-reveal-item": true } : {})}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-3"
            >
              {FEATURES.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon className="h-4 w-4 shrink-0 text-accent" aria-hidden />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            {...(reveal ? { "data-reveal-item": true } : {})}
            className="relative"
            aria-hidden="true"
          >
            <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_24px_60px_-28px_rgba(15,23,42,0.35)] ring-1 ring-black/5">
              <div className="flex items-center gap-2 border-b border-border/60 bg-muted/40 px-3 py-2.5">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="ml-2 flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
                  <span
                    className="hidden w-[72px] shrink-0 sm:block"
                    style={{
                      aspectRatio: `${LOGO_HEADER_INTRINSIC.width} / ${LOGO_HEADER_INTRINSIC.height}`,
                    }}
                  >
                    <LogoHeaderImage className="size-full max-h-5" />
                  </span>
                  <span className="text-[10px] font-bold tracking-wide text-accent sm:hidden">
                    KRYAJET
                  </span>
                  <nav className="hidden items-center gap-1 md:flex">
                    <span className="rounded-md px-2 py-1 text-[10px] text-muted-foreground">Home</span>
                    <span className="rounded-full bg-accent px-2.5 py-1 text-[10px] font-medium text-accent-foreground">
                      Search Caterers
                    </span>
                    <span className="rounded-md px-2 py-1 text-[10px] text-muted-foreground">
                      Favourites
                    </span>
                    <span className="relative rounded-md px-2 py-1 text-[10px] text-muted-foreground">
                      Messages
                      <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 px-0.5 text-[8px] font-semibold text-white">
                        6
                      </span>
                    </span>
                  </nav>
                  <div className="ml-auto flex items-center gap-2">
                    <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="h-6 w-6 rounded-full bg-gradient-to-br from-accent/80 to-accent" />
                  </div>
                </div>
              </div>

              <div className="grid min-h-[280px] sm:min-h-[320px] md:grid-cols-[minmax(180px,38%)_1fr]">
                <div className="flex flex-col border-b border-border/60 bg-background md:border-b-0 md:border-r">
                  <div className="border-b border-border/50 p-2.5">
                    <div className="flex h-8 items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2.5 text-[10px] text-muted-foreground">
                      <Search className="h-3 w-3 shrink-0" />
                      <span className="truncate">Search airport, ICAO, IATA, city...</span>
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-2 border-b border-border/40 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-[11px] font-semibold text-foreground">
                        Paris-Le Bourget Airport
                      </p>
                      <p className="mt-0.5 truncate text-[9px] text-muted-foreground">
                        Paris, France · LBG · LFPB
                      </p>
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-0.5 text-[9px] font-medium text-muted-foreground">
                      <ArrowLeft className="h-2.5 w-2.5" />
                      Map
                    </span>
                  </div>

                  <div className="flex-1 space-y-2 p-2.5">
                    <p className="px-1 text-[9px] text-muted-foreground">Showing 1 caterer</p>
                    <div className="rounded-xl border border-border/70 bg-card p-2.5 shadow-sm">
                      <div className="flex gap-2">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-accent/10 text-[9px] font-bold text-accent">
                          AC
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[11px] font-semibold">Atelier Cabin</p>
                          <p className="mt-0.5 line-clamp-2 text-[9px] leading-snug text-muted-foreground">
                            Fine dining & hangar delivery for Le Bourget FBOs.
                          </p>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            <span className="rounded-md bg-emerald-600/90 px-1.5 py-0.5 text-[8px] font-medium text-white">
                              24/7
                            </span>
                            <span className="rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[8px] text-muted-foreground">
                              Delivery
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-border/50 p-2.5">
                    <div className="flex h-8 items-center justify-center rounded-lg border border-border/70 bg-muted/40 text-[10px] font-medium text-muted-foreground">
                      View full airport page
                    </div>
                  </div>
                </div>

                <div className="relative min-h-[220px] overflow-hidden bg-[#dceef8] sm:min-h-[280px]">
                  <img
                    src={catererMapEurope}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    draggable={false}
                  />
                  <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full border border-accent/20 bg-accent/10 px-2.5 py-1 text-[9px] font-medium text-accent backdrop-blur-sm">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                    </span>
                    Live global network
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
